import { useContext, useMemo, useReducer, useState } from 'react'
import {
  WppButton,
  WppInlineMessage,
  WppInput,
  WppModal,
  WppSelect,
  WppTypography,
} from '@wppopen/components-library-react'
import projectFormReducer, {
  defaultNewProjectErrorObject,
  emptyProjectFormReducerState,
  projectFormHelperFunctions,
  NewProjectInputField,
  NewProjectSelectField,
  initialProjectFormReducerState,
} from 'reducers/CreateAndEditNewProjectReducer/CreateAndEditNewProjectReducer'
import appContext from 'context/appContext'
import { getType } from 'utils'
import { ERROR_MESSAGES } from 'utils/constants'
import { renderEmptyState, renderSelectItem } from '../helpers'
import type { WppSelectCustomEvent } from '@wppopen/components-library/dist/types/components'
import type { SelectChangeEventDetail } from '@wppopen/components-library'
import type { ProjectsCreateBody, ProjectsDetailResult } from '@contracts'
import type { FEApiError } from 'utils/errorHandling'
import clsx from 'clsx'
import styles from '../Modal.module.scss'

interface CreateAndEditNewProjectProps {
  isModalOpen: boolean
  isApiLoading: boolean
  onCancel: () => void
  onSubmit: (data: ProjectsCreateBody) => Promise<void>
  projectData?: ProjectsDetailResult
}

export default function CreateAndEditNewProject({
  isModalOpen,
  isApiLoading,
  onCancel,
  onSubmit,
  projectData,
}: CreateAndEditNewProjectProps) {
  const { importedClients } = useContext(appContext)
  const [errorMessage, setErrorMessage] = useState<string>()
  const initialProjectFormState = useMemo(
    () => (!!projectData ? initialProjectFormReducerState(projectData) : emptyProjectFormReducerState),
    [projectData],
  )
  const [formReducerState, formReducerDispatch] = useReducer(projectFormReducer, initialProjectFormState)
  const { project_name, client_id, brand_id } = formReducerState
  const { updateSingleInput, updateSelect, handleError } = projectFormHelperFunctions

  // get the list of brands based on the selected client
  const listOfBrands = useMemo(() => {
    if (!client_id.value || !importedClients) return
    // reset brand_id value if client_id changes
    if (!projectData) formReducerDispatch(updateSelect('brand_id', ''))
    const selectedClient = importedClients.find(({ id }) => id === client_id.value)
    return selectedClient?.brands
  }, [client_id.value])

  // handle the select change event
  const handleSelect = (e: WppSelectCustomEvent<SelectChangeEventDetail>) => {
    // prevent the select change event from firing if the projectData is present because user should not be able to change the client or brand on edit project details
    if (!!projectData) return

    const { value, name } = e.target
    const typeSafeInputFieldKey = name as NewProjectSelectField
    formReducerDispatch(updateSelect(typeSafeInputFieldKey, value))

    if (formReducerState[typeSafeInputFieldKey].error && !!value)
      formReducerDispatch(handleError(typeSafeInputFieldKey, undefined))
    else if (!formReducerState[typeSafeInputFieldKey].error && !value)
      formReducerDispatch(handleError(typeSafeInputFieldKey, ERROR_MESSAGES.MANDATORY_FIELD))
  }

  // handle the form submission
  const handleSubmitForm = () => {
    setErrorMessage(undefined)
    const requiredFields = defaultNewProjectErrorObject.requiredField?.map(({ id }) => id) || []
    let isFormValid = true

    requiredFields.forEach(field => {
      const { value, error } = formReducerState[field]

      // if value is empty or there is an error message
      if (!value || !!error) {
        // if value is NOT empty and there is an error message, remove the error message otherwise set the error message
        formReducerDispatch(handleError(field, !!value ? undefined : ERROR_MESSAGES.MANDATORY_FIELD))
        // if value is empty, make the form invalid
        if (!value) return (isFormValid = false)
      }
    })

    if (!isFormValid) return

    const projectFormData: ProjectsCreateBody = {
      project_name: project_name.value,
      client_id: client_id.value,
      brand_id: brand_id.value,
    }

    onSubmit(projectFormData).catch((err: FEApiError) => {
      const { errors, message } = err

      if (errors) {
        errors.forEach(({ path, message }) => {
          formReducerDispatch(handleError(path as NewProjectInputField, message))
        })
      } else if (message) {
        setErrorMessage(message)
      }
    })
  }

  return (
    <WppModal
      open={isModalOpen}
      formConfig={{
        onSubmit: e => {
          e.preventDefault()
          handleSubmitForm()
        },
        onReset: () => onCancel(),
      }}
      className={clsx(styles.modal_reset_overflow, styles.modal)}
    >
      <WppTypography tag="h3" type="l-strong" slot="header">
        {projectData ? 'Edit' : 'Create new'} project
      </WppTypography>

      <div slot="body">
        <WppInput
          data-testid="project-name-input"
          placeholder="Enter project name e.g Q3 Makeup Launch"
          labelConfig={{ text: 'Project name' }}
          required
          name={getType<NewProjectInputField>('project_name')}
          value={project_name.value}
          disabled={isApiLoading}
          onWppChange={e => {
            const { value, name } = e.target
            const typeSafeInputFieldKey = name as NewProjectInputField
            formReducerDispatch(updateSingleInput(typeSafeInputFieldKey, value))
            // if project_name has an error message and the user starts typing, remove the error message
            if (project_name.error || !!value) formReducerDispatch(handleError('project_name', undefined))
            else if (!project_name.error && !value)
              formReducerDispatch(handleError('project_name', ERROR_MESSAGES.MANDATORY_FIELD))
          }}
          messageType={!project_name.error ? undefined : 'error'}
          message={project_name.error || ''}
        />

        <WppSelect
          data-testid="client-select"
          name={getType<NewProjectSelectField>('client_id')}
          labelConfig={{ text: 'Client' }}
          required
          placeholder="Please select"
          disabled={isApiLoading || (!!client_id.value && !!projectData)}
          withSearch={false}
          value={client_id.value}
          onWppChange={e => handleSelect(e)}
          messageType={!client_id.error ? undefined : 'error'}
          message={client_id.error || ''}
        >
          {/* if projectData exist just print the single option */}
          {!!projectData
            ? renderSelectItem(
                client_id.value,
                importedClients?.find(({ id }) => id === client_id.value)?.client_name || 'NOT FOUND',
                'client-options',
              )
            : importedClients?.map(({ id, client_name }) => renderSelectItem(id!, client_name, 'client-options'))}
        </WppSelect>

        <WppSelect
          data-testid="brand-select"
          name={getType<NewProjectSelectField>('brand_id')}
          labelConfig={{ text: 'Brand' }}
          required
          placeholder="Please select"
          disabled={isApiLoading || !client_id.value || (!!brand_id.value && !!projectData)}
          withSearch={false}
          value={brand_id.value}
          onWppChange={e => handleSelect(e)}
          messageType={!brand_id.error ? undefined : 'error'}
          message={brand_id.error || ''}
        >
          {/* if projectData exist just print the single option */}
          {!!projectData
            ? renderSelectItem(
                brand_id.value,
                listOfBrands?.find(({ id }) => id === brand_id.value)?.brand_name || 'NOT FOUND',
                'brand-options',
              )
            : // if there is no projectData, print the list of brands based on the selected client
              listOfBrands?.length
              ? listOfBrands.map(({ id, brand_name }) => renderSelectItem(id!, brand_name, 'brand-options'))
              : // if there is no brands for the selected client and the is no projectData, print the empty option
                renderEmptyState('No brands configured for client', 'empty-brand-option')}
        </WppSelect>
      </div>

      <div slot="actions">
        <div className={styles.modal__actionsGroup}>
          <WppButton type="reset" variant="secondary" data-testid="modal-reset-button" disabled={isApiLoading}>
            Cancel
          </WppButton>
          <WppButton type="submit" variant="primary" data-testid="modal-submit-button" loading={isApiLoading}>
            {!projectData ? 'Create' : 'Save'}
          </WppButton>
        </div>
        {errorMessage && <WppInlineMessage size="s" message={errorMessage} type="error" />}
      </div>
    </WppModal>
  )
}
